import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  FormGroup,
  CircularProgress,
  Alert,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Stack, TextField, InputAdornment } from "@mui/material";
import { getAllCustomers, addCustomerOnPrintyo, getCustomers } from "../../api";
import LinearProgress from "@mui/material/LinearProgress";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import Layout from "../../components/Layout";
import Billing from "../order/components/Billing";
import Shipping from "../order/components/Shipping";
import { Formik, Form } from "formik";

function CustomerTypeBadge({ type }) {
  if (!type) return <>-</>;
  const styles = {
    new:      { bg: "#1e7e34", color: "#fff", border: "1px solid #155724" },
    old:      { bg: "#3949ab", color: "#fff", border: "1px solid #2c3899" },
    vip:      { bg: "#fff8e1", color: "#f57f17", border: "1px solid #ffe082" },
    inactive: { bg: "#f5f5f5", color: "#757575", border: "1px solid #d0d0d0" },
  };
  const s = styles[type.toLowerCase()] || { bg: "#f0f0f0", color: "#444", border: "1px solid #ccc" };
  return (
    <span style={{
      backgroundColor: s.bg,
      color: s.color,
      border: s.border,
      padding: "3px 12px",
      borderRadius: "12px",
      fontSize: "12px",
      fontWeight: 700,
      display: "inline-block",
      textTransform: "capitalize",
    }}>
      {type}
    </span>
  );
}

function EnhancedTableHead(props) {
  const { onSelectAllClick, numSelected, rowCount } = props;

  return (
    <TableHead>
      <TableRow>
        <TableCell>User Id</TableCell>
        <TableCell>Name</TableCell>
        <TableCell>Email</TableCell>
        <TableCell>Phone no.</TableCell>
        <TableCell>Total Order Amount</TableCell>
        <TableCell>Customer Type</TableCell>
      </TableRow>
    </TableHead>
  );
}

const customerFormInitialValues = {
  billing_first_name: "",
  billing_last_name: "",
  billing_country: "",
  billing_address_1: "",
  billing_address_2: "",
  billing_city: "",
  billing_company: "",
  billing_email: "",
  billing_phone: "",
  billing_postcode: "",
  billing_state: "",
  shipping_first_name: "",
  shipping_last_name: "",
  shipping_country: "",
  shipping_address_1: "",
  shipping_address_2: "",
  shipping_city: "",
  shipping_company: "",
  shipping_phone: "",
  shipping_postcode: "",
  shipping_state: "",
};

export default function EnhancedTable() {
  const [page, setPage] = React.useState(0);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customercount, setCustomerCount] = useState(0);
  const [searchvalue, setSearchValue] = useState("");
  const [search, setSearch] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  // Add customer dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [addingCustomer, setAddingCustomer] = useState(false);
  const [podcustomer, setPodCustomer] = useState(false);
  const [billingchecked, setBillingChecked] = useState(true);
  const [newCustomerUsername, setNewCustomerUsername] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [dialogError, setDialogError] = useState("");
  const [usernameChecking, setUsernameChecking] = useState(false);
  const [alertString, setAlertString] = useState("");
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState("success");

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        setLoading(true);
        const data = await getAllCustomers(page + 1, 20, search);
        setCustomers(data.response);
        setLoading(false);
        setCustomerCount(data.count);
      } catch (error) {
        console.log(error);
        setLoading(false);
      }
    };
    fetchCustomers();
  }, [page, search, refreshKey]);

  // Debounced username uniqueness check
  useEffect(() => {
    const username = newCustomerUsername.trim();
    if (!username) {
      setUsernameError("");
      setUsernameChecking(false);
      return;
    }
    setUsernameChecking(true);
    setUsernameError("");
    const timer = setTimeout(async () => {
      try {
        const results = await getCustomers(username);
        const exists = results?.some(
          (c) => c.username && c.username.toLowerCase() === username.toLowerCase()
        );
        setUsernameError(exists ? "This username already exists in WordPress" : "");
      } catch (error) {
        console.log("Username check failed", error);
      }
      setUsernameChecking(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [newCustomerUsername]);

  const handleAddCustomer = async (values) => {
    setAddingCustomer(true);
    const metadata = [];
    if (podcustomer) {
      metadata.push({ key: "pod_customer", value: "yes" });
    }

    let shipping = {};
    if (billingchecked || !values.shipping_first_name) {
      shipping = {
        first_name: values.billing_first_name,
        last_name: values.billing_last_name,
        address_1: values.billing_address_1,
        address_2: values.billing_address_2,
        city: values.billing_city,
        state: values.billing_state,
        postcode: values.billing_postcode,
        country: values.billing_country,
        company: values.billing_company,
      };
    } else {
      shipping = {
        first_name: values.shipping_first_name,
        last_name: values.shipping_last_name,
        address_1: values.shipping_address_1,
        address_2: values.shipping_address_2,
        city: values.shipping_city,
        state: values.shipping_state,
        postcode: values.shipping_postcode,
        country: values.shipping_country,
        company: values.shipping_company,
      };
    }

    const billing = {
      first_name: values.billing_first_name,
      last_name: values.billing_last_name,
      address_1: values.billing_address_1,
      address_2: values.billing_address_2,
      city: values.billing_city,
      state: values.billing_state,
      postcode: values.billing_postcode,
      country: values.billing_country,
      email: values.billing_email,
      phone: values.billing_phone,
      company: values.billing_company,
    };

    const filteredBilling = Object.fromEntries(
      Object.entries(billing).filter(([, v]) => v && v.trim() !== "")
    );
    const filteredShipping = Object.fromEntries(
      Object.entries(shipping).filter(([, v]) => v && v.trim() !== "")
    );

    try {
      const response = await addCustomerOnPrintyo({
        data: {
          ...(Object.keys(filteredBilling).length > 0 && { billing: filteredBilling }),
          ...(Object.keys(filteredShipping).length > 0 && { shipping: filteredShipping }),
          email: values.billing_email,
          first_name: values.billing_first_name,
          last_name: values.billing_last_name,
          username: newCustomerUsername.trim(),
          meta_data: metadata,
        },
      });
      const returnresponse = response?.response;
      if (returnresponse?.data?.status === 400 || returnresponse?.code) {
        setDialogError(returnresponse.message || "Failed to create customer");
      } else if (returnresponse?.id) {
        setDialogError("");
        setDialogOpen(false);
        setNewCustomerUsername("");
        setPodCustomer(false);
        setBillingChecked(true);
        setRefreshKey((k) => k + 1);
        setAlertString("Customer Created Successfully");
        setAlertSeverity("success");
        setAlertOpen(true);
      }
    } catch (error) {
      console.log(error);
      setDialogError("Something went wrong while adding customer");
    }
    setAddingCustomer(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setSearch(searchvalue);
    setPage(0);
  };

  const handleSearch = (e) => {
    setSearchValue(e.target.value);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setNewCustomerUsername("");
    setUsernameError("");
    setDialogError("");
    setPodCustomer(false);
    setBillingChecked(true);
  };

  return (
    <Layout>
      <Box
        bgcolor="#f0f0f1"
        minHeight={`calc(100vh - 67px)`}
        px={3}
        py={3}
        className={`secBox`}
      >
        {alertOpen && (
          <Alert
            severity={alertSeverity}
            sx={{ mb: 2 }}
            onClose={() => setAlertOpen(false)}
          >
            {alertString}
          </Alert>
        )}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography className="heading">Customers</Typography>
          <Button variant="contained" sx={{ textTransform: "none" }} onClick={() => setDialogOpen(true)}>
            Add Customer
          </Button>
        </Box>
        <Stack direction="row" justifyContent="flex-end" mt={2}>
          <form onSubmit={handleSearchSubmit}>
            <TextField
              id="outlined-start-adornment"
              className={`searchinput`}
              sx={{
                m: 1,
                width: "30ch",
                backgroundColor: "#fff",
                fontSize: "14px",
              }}
              size="small"
              placeholder="Search Customer"
              onChange={handleSearch}
              value={searchvalue}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon onClick={handleSearchSubmit} />
                  </InputAdornment>
                ),
              }}
            />
          </form>
        </Stack>
        <Paper className={`paper`}>
          {loading && <LinearProgress />}
          <TableContainer>
            <Table
              className={`table custom-table`}
              aria-labelledby="tableTitle"
              size="small"
              aria-label="enhanced table"
            >
              <EnhancedTableHead
                rowCount={customercount && customercount.length}
              />
              <TableBody>
                {customers ? (
                  customers.map((row, index) => {
                    const labelId = `enhanced-table-checkbox-${index}`;
                    return (
                      <TableRow hover tabIndex={-1} key={index}>
                        <TableCell>
                          <Link
                            to={`/customers/${row.user_id}/details`}
                            style={{
                              color: "#1976d2",
                              textDecoration: "none",
                              cursor: "pointer",
                            }}
                          >
                            {row.user_id}
                          </Link>
                        </TableCell>
                        <TableCell
                          id={labelId}
                          scope="row"
                          className={`orderId`}
                        >
                          {row.name}
                        </TableCell>
                        <TableCell>{row.user_email}</TableCell>
                        <TableCell>{row.billing_phone}</TableCell>
                        <TableCell>
                          {row.total_order_amount != null ? `$${row.total_order_amount}` : "-"}
                        </TableCell>
                        <TableCell>
                          <CustomerTypeBadge type={row.customer_type} />
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow hover tabIndex={-1} key={0}>
                    <TableCell colSpan={6} align="center">
                      No Customer(s) Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            {loading && <LinearProgress />}
          </TableContainer>
          <TablePagination
            component="div"
            count={customercount}
            labelRowsPerPage=""
            rowsPerPage={20}
            page={page}
            onPageChange={handleChangePage}
          />
        </Paper>
      </Box>

      {/* Add Customer Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add New Customer</DialogTitle>
        <Formik
          initialValues={customerFormInitialValues}
          onSubmit={handleAddCustomer}
        >
          {({ errors, values, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                {dialogError && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDialogError("")}>
                    {dialogError}
                  </Alert>
                )}
                <Box mb={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Username"
                    placeholder="Enter username"
                    value={newCustomerUsername}
                    onChange={(e) => {
                      const val = e.target.value;
                      setNewCustomerUsername(val);
                      const parts = val.trim().split(/\s+/);
                      setFieldValue("billing_first_name", parts[0] || "");
                      setFieldValue("billing_last_name", parts.length > 1 ? parts.slice(1).join(" ") : "");
                    }}
                    error={!!usernameError}
                    helperText={
                      usernameChecking ? "Checking username..." : usernameError
                    }
                    InputProps={{
                      endAdornment: usernameChecking ? (
                        <CircularProgress color="inherit" size={18} />
                      ) : null,
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    opacity: usernameChecking ? 0.4 : 1,
                    pointerEvents: usernameChecking ? "none" : "auto",
                    transition: "opacity 0.2s",
                  }}
                >
                  <Billing
                    errors={errors}
                    values={values}
                    setFieldValue={setFieldValue}
                  />
                  <Box mt={2}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={billingchecked}
                          onChange={(e) => setBillingChecked(e.target.checked)}
                        />
                      }
                      label="Shipping same as billing"
                    />
                  </Box>
                  {!billingchecked && (
                    <Shipping
                      errors={errors}
                      values={values}
                      setFieldValue={setFieldValue}
                    />
                  )}
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={podcustomer}
                          onChange={(e) => setPodCustomer(e.target.checked)}
                        />
                      }
                      label="Pod Customer"
                    />
                  </FormGroup>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseDialog} sx={{ textTransform: "none" }}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ textTransform: "none" }}
                  disabled={
                    addingCustomer ||
                    !!usernameError ||
                    usernameChecking ||
                    !newCustomerUsername.trim()
                  }
                >
                  {addingCustomer ? "Adding..." : "Add Customer"}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </Layout>
  );
}
